"use client";

import React from "react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

/** Mirror the tooltip placements that were previously shared via `TooltipWrapper`. */
type TooltipPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "accent"
  | "neutral"
  | "success"
  | "error"
  | "warning"
  | "info"
  | "plain"
  | "base";

export type ButtonSize = "xs" | "sm" | "md" | "lg";
export type ButtonState = "default" | "loading";

interface ButtonBaseProps {
  tooltip?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  iconOnly?: boolean;
  customClass?: string;
  tooltipPlacement?: TooltipPlacement;
  tooltipTarget?: "self" | null;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}

type ButtonLinkProps = {
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  download?: string | boolean;
};

export type ButtonProps = ButtonBaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonLinkProps;

const sizeClasses: Record<ButtonSize, string> = {
  xs: "px-1.5 py-0.5 text-xs",
  sm: "px-2.5 py-0.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
};

const variantClasses: Record<ButtonVariant, string> = {
  plain: "bg-transparent no-noise shadow-none border-none hover:bg-base-content/10",
  primary: "text-primary-content primary",
  secondary: "text-secondary-content secondary",
  accent: "text-accent-content accent",
  neutral: "text-neutral-content neutral",
  success: "text-success-content success",
  error: "text-error-content error",
  warning: "text-warning-content warning",
  info: "text-info-content info",
  base: "text-base-content base-200",
};

const baseClasses =
  "inline-flex items-center justify-center font-medium rounded-field transition-all duration-300 focus:outline-none outline-none";

const tooltipPlacementClasses: Record<TooltipPlacement, string> = {
  top: "tooltip-top",
  "top-start": "tooltip-top",
  "top-end": "tooltip-top",
  bottom: "tooltip-bottom",
  "bottom-start": "tooltip-bottom",
  "bottom-end": "tooltip-bottom",
  left: "tooltip-left",
  "left-start": "tooltip-left",
  "left-end": "tooltip-left",
  right: "tooltip-right",
  "right-start": "tooltip-right",
  "right-end": "tooltip-right",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      tooltip,
      variant = "primary",
      size = "sm",
      state = "default",
      disabled = false,
      type = "button",
      iconOnly = false,
      customClass,
      tooltipPlacement = "top",
      tooltipTarget,
      className,
      children,
      href,
      target,
      rel,
      download,
      onClick,
      ...props
    },
    ref
  ) => {
    const isTooltipSelf = tooltipTarget === "self";
    const isLink = typeof href === "string" && href.length > 0;

    const resolvedRel =
      rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);

    const buttonClasses = twMerge(
      clsx(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        iconOnly && "aspect-square p-1",
        disabled && "opacity-50 cursor-not-allowed",
        customClass,
        className
      )
    );

    const tooltipText =
      typeof tooltip === "string" || typeof tooltip === "number"
        ? tooltip.toString()
        : undefined;
    const tooltipDirectionClass = tooltipPlacementClasses[tooltipPlacement];

    const innerContent = (
      <>
        {state === "loading" ? (
          <i className="fi fi-ss-spinner animate-spin text-xs" aria-hidden="true" />
        ) : null}
        {children}
        {!isTooltipSelf && tooltipText ? (
          <span
            className={clsx(
              "tooltip cursor-help ml-1 text-xs aspect-square flex items-center justify-center text-white/60 focus:ring-0 rounded-full text-center",
              tooltipDirectionClass
            )}
            data-tip={tooltipText}
            aria-label={tooltipText}
          >
            <i className="fi fi-ss-interrogation" aria-hidden="true" />
          </span>
        ) : null}
      </>
    );

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      onClick?.(event);

      if (event.defaultPrevented || !isLink || disabled) {
        return;
      }

      if (typeof document === "undefined" || !href) {
        return;
      }

      const link = document.createElement("a");
      link.href = href;

      if (target) {
        link.target = target;
      }

      if (resolvedRel) {
        link.rel = resolvedRel;
      }

      if (typeof download !== "undefined" && download !== false) {
        link.download = download === true ? "" : download;
      }

      link.click();
    };

    const buttonContent = (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={disabled}
        aria-busy={state === "loading" || undefined}
        onClick={handleClick}
        {...props}
      >
        {innerContent}
      </button>
    );

    if (isTooltipSelf && tooltipText) {
      return (
        <span
          className={clsx("tooltip inline-flex", tooltipDirectionClass)}
          data-tip={tooltipText}
          aria-label={tooltipText}
        >
          {buttonContent}
        </span>
      );
    }

    return buttonContent;
  }
);

Button.displayName = "Button";

export default Button;
